import React, { useEffect, useState } from 'react';
import '../../componentsStyling/ContentStyling.css';
import { DataGrid } from "@mui/x-data-grid";
import EditUser from './EditUser.js';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useUserApis } from '../../apis/UsersAPIs.js';
import AddUser from './AddUser.js';
import CircularProgress from '@mui/material/CircularProgress';
import SearchBar from '../general/SearchBar.js'; // Import the SearchBar component
import useWindowSize from '../../utils/windowSize.js';

function Users({ userRole }) {
    const { fetchUsers, deleteUser, updateUser, addUser } = useUserApis();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]); // For filtered users
    const [searchTerm, setSearchTerm] = useState(""); // For search input
    const [open, setOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUsers();
    }, []);

    useEffect(() => {
        // Filter users dynamically based on search term
        const lowerCaseSearch = searchTerm.toLowerCase();
        setFilteredUsers(
            users.filter(
                (user) =>
                    (user.username && user.username.toLowerCase().includes(lowerCaseSearch)) || // Filter by username
                    (user.role && user.role.toLowerCase().includes(lowerCaseSearch)) || // Filter by role
                    (user.studentId && user.studentId.toString().includes(lowerCaseSearch)) ||
                    (user.id && user.id.toString().includes(lowerCaseSearch)) // Convert ID to string for filtering
            )
        );
    }, [searchTerm, users]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const usersWithId = await fetchUsers();
            setUsers(usersWithId);
            setFilteredUsers(usersWithId); // Initialize filtered users
        } catch (err) {
            console.error("Error loading users:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term); // Update search term for filtering
    };

    const onDelClick = (event, user) => {
        event.stopPropagation(); // Prevent row click event from firing

        // Check deletion permissions
        if (userRole === 'ROLE_STAFF' && (user.role === 'ADMIN' || user.role === 'STAFF')) {
            alert('You do not have permission to delete this user.');
            return;
        }

        if (window.confirm("Are you sure you want to delete this user?")) {
            deleteUser(user.id).then(success => {
                if (success) {
                    loadUsers();
                    setOpen(true); // Show Snackbar on successful delete
                } else {
                    alert('Something went wrong!');
                }
            });
        }
    };

    const handleCloseSnackbar = () => {
        setOpen(false);
    };

    const handleRowClick = (params) => {
        setSelectedRowData(params.row);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedRowData(null);
    };

    const handleAddDialogOpen = () => {
        if (userRole === 'ROLE_ADMIN' || userRole === 'ROLE_STAFF') {
            setAddDialogOpen(true);
        } else {
            alert('You do not have permission to add users.');
        }
    };

    const handleAddDialogClose = () => {
        setAddDialogOpen(false);
    };

    const handleAddUser = async (newUser) => {
        try {

            const result = await addUser(newUser);

            alert(result.message)

            if (result.success) {
                loadUsers(); // Reload user list after adding
                setAddDialogOpen(false); // Close dialog
            } 

        }  catch (error) {
            console.error("Error adding user or updating student:", error);
            alert("Unexpected error occurred");
        }
    };

    const columnDefs = [
        { field: 'id', headerName: 'User ID', width: 150 },
        { field: 'username', headerName: 'Username', width: 200 },
        { field: 'role', headerName: 'Role', width: 150 },
        { field: 'studentId', headerName: 'Student ID', width: 200 },
        { field: 'activationStatus', headerName: 'Activation Status', width: 150 },
        {
            field: '_links.self.href',
            headerName: '',
            sortable: false,
            filterable: false,
            width: 75,
            renderCell: (row) => {
                const canEdit = userRole === 'ROLE_ADMIN' || (userRole === 'ROLE_STAFF' && row.row.role === 'STUDENT');
                return (
                    canEdit ? (
                        <div onClick={(event) => {
                            event.stopPropagation(); // Prevent row click event
                        }}>
                            <EditUser
                                data={row}
                                userRole={userRole}
                                updateUser={(user, link) => {
                                    if (userRole === 'ROLE_ADMIN' || (userRole === 'ROLE_STAFF' && user.role === 'STUDENT')) {
                                        updateUser(user, link).then(success => {
                                            if (success) loadUsers();
                                            else alert('Something went wrong!');
                                        });
                                    } else {
                                        alert('You do not have permission to make that change.');
                                    }
                                }}
                            />
                        </div>
                    ) : null // Return null if the user can't edit
                );
            },
        },
        {
            field: '_links.user.href',
            headerName: '',
            sortable: false,
            filterable: false,
            width: 75,
            renderCell: (params) => {
                // old version where staff could delete students, switched so only admins can delete users
                //const canDelete = userRole === 'ROLE_ADMIN' || (userRole === 'ROLE_STAFF' && params.row.role === 'STUDENT');
                const canDelete = userRole === 'ROLE_ADMIN';
                return (
                    canDelete ? (
                        <IconButton onClick={(event) => onDelClick(event, params.row)}>
                            <DeleteIcon color="error" />
                        </IconButton>
                    ) : null // Return null if the user can't delete
                );
            },
        }
    ];

    // Calculate dynamic table height for desktop/laptop, accounting for components above
    const { width, height } = useWindowSize();
    const isSmallScreen = width < 900;
    // Header: ~85px, Title container: ~60px, SearchBar: ~60px, Padding: ~50px
    // Reduced subtraction to make tables extend down and push footer below viewport
    const tableHeight = !isSmallScreen 
        ? Math.max(400, height - 205) // Minimum 400px, subtract only components above table
        : 600; // Keep fixed height for mobile

    if (loading) {
        return (
            <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </div>
        );
    }
    return (
        <div>
            <div id="titleContainer">
                <h1 style={{margin:'5px'}}>Users</h1>

                {/* Disabling button as all users should first have a student account */}
                {/* {userRole !== "ROLE_STAFF" && (
                    <button className="add-button" onClick={handleAddDialogOpen}>Add</button>
                )} */}
            </div>

            {/* Add the SearchBar */}
            <SearchBar onSearch={handleSearch} />

            <div className="table-container" >
                <div className="dataGridContainer" style={{ width: '100%', maxWidth: 'min-content', height: `${tableHeight}px` }}>
                    <DataGrid
                        rows={filteredUsers} // Use filtered users
                        columns={columnDefs}
                        disableSelectionOnClick={true}
                        getRowId={row => row._links.self.href}
                        rowHeight={80}
                        onRowClick={handleRowClick}
                    />
                </div>
            </div>

            {/* Snackbar Component */}
            <Snackbar
                open={open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message="User deleted successfully"
            />

            {/* Dialog to show row data */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>User Details</DialogTitle>
                <DialogContent>
                    {selectedRowData && (
                        <div>
                            {Object.entries(selectedRowData).map(([key, value]) => (
                                <div key={key} style={{ margin: '10px 0' }}>
                                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value ? value.toString() : "N/A"}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
            
            <AddUser
                open={addDialogOpen}
                onClose={handleAddDialogClose}
                onSave={handleAddUser}
                userRole={userRole}
            />
        </div>
    );
}

export default Users;
